// FRESHMOD 2026/10 - Grabado de alianzas
// - <engraving-form>: despliegue, contador, saneado del texto y aceptación obligatoria.
// - Bloquea el envío del formulario de producto (ficha, barra fija y quick view) mientras falte algo.
// - Cobro: al añadir, el anillo y el "Servicio de grabado" viajan en la misma petición, unidos por _grabado_ref.
// - Carrito: el servicio sigue la cantidad de su anillo; se quitan servicios huérfanos y grabados sin aceptación.

(function () {
  // Caracteres que se pueden grabar (cliente: solo mayúsculas y caracteres sencillos, sin , / &).
  // Basta con editar esta cadena.
  const ENGRAVING_ALLOWED_CHARS = "ABCDEFGHIJKLMNÑOPQRSTUVWXYZ0123456789 .-'";

  const ENGRAVING_PROPERTIES = ['Grabado', '_grabado', '_grabado_aceptado', '_grabado_ref', '_grabado_servicio'];

  const engravingScript = document.currentScript;
  const SERVICE_HANDLE = engravingScript?.dataset.engravingServiceHandle || 'servicio-de-grabado';

  // Everything is engraved in capitals: the text is uppercased and accents are dropped (Á → A), keeping the Ñ.
  // NFC joins accents typed as two code points; typographic apostrophes (iOS) become plain ones.
  const toEngravingChar = char => (char === 'Ñ' ? char : char.normalize('NFD').replace(/[̀-ͯ]/g, ''));

  const sanitize = value =>
    Array.from(value.normalize('NFC').replace(/[‘’]/g, "'").toLocaleUpperCase('es'))
      .map(toEngravingChar)
      .filter(char => ENGRAVING_ALLOWED_CHARS.includes(char))
      .join('');

  // True when sanitizing dropped characters (not only uppercased them), so the customer is told why.
  const removedChars = (value, sanitized) => Array.from(sanitized).length < Array.from(value.normalize('NFC')).length;

  const normalizeSpaces = value => value.replace(/\s+/g, ' ').trim();

  const createRef = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

  if (!customElements.get('engraving-form')) {
    class EngravingForm extends HTMLElement {
      connectedCallback() {
        if (!this.initialized) this.init();
        if (!this.initialized) return;

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          // The sticky cart bar is re-rendered on variant change, so it has to be locked again.
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => this.updateLock());
        }
        this.updateLock();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      init() {
        this.form = this.closest('form');
        if (!this.form) return;

        this.toggle = this.querySelector('[data-engraving-toggle]');
        this.panel = this.querySelector('[data-engraving-panel]');
        this.textInput = this.querySelector('[data-engraving-text]');
        this.counter = this.querySelector('[data-engraving-counter]');
        this.textMessage = this.querySelector('[data-engraving-text-message]');
        this.flagInput = this.querySelector('[data-engraving-flag]');
        this.acceptInput = this.querySelector('[data-engraving-accept]');
        this.acceptedAtInput = this.querySelector('[data-engraving-accepted-at]');
        this.acceptMessage = this.querySelector('[data-engraving-accept-message]');
        this.variantLabels = JSON.parse(this.querySelector('[data-engraving-labels]')?.textContent || '{}');

        this.toggle.addEventListener('change', this.onToggle.bind(this));
        this.textInput.addEventListener('input', this.onTextInput.bind(this));
        this.acceptInput.addEventListener('change', this.onAccept.bind(this));
        // product-form and product-card build `new FormData(form)` on submit: this is where the service line is added.
        this.form.addEventListener('formdata', this.onFormData.bind(this));

        this.initialized = true;
        this.hidden = false;
        this.setOpen(this.toggle.checked);
      }

      onToggle() {
        this.setOpen(this.toggle.checked);
        if (this.toggle.checked) this.textInput.focus();
      }

      // Closed = every input disabled, so the form sends no engraving property at all.
      setOpen(open) {
        this.panel.hidden = !open;
        this.textInput.disabled = !open;
        this.flagInput.disabled = !open;
        this.acceptInput.disabled = !open;
        this.acceptedAtInput.disabled = !(open && this.acceptInput.checked);

        if (!open) {
          this.hideMessage(this.textMessage);
          this.hideMessage(this.acceptMessage);
          this.textInput.removeAttribute('aria-invalid');
          this.acceptInput.removeAttribute('aria-invalid');
        }
        this.updateCounter();
        this.updateLock();
      }

      onTextInput() {
        const { value } = this.textInput;
        const sanitized = sanitize(value);

        if (sanitized !== value) {
          const caret = sanitize(value.slice(0, this.textInput.selectionStart ?? value.length)).length;
          this.textInput.value = sanitized;
          this.textInput.setSelectionRange(caret, caret);
        }
        if (removedChars(value, sanitized)) {
          this.showMessage(this.textMessage, this.dataset.messageSanitized);
        } else {
          this.hideMessage(this.textMessage);
        }

        this.textInput.removeAttribute('aria-invalid');
        this.updateCounter();
        this.updateLock();
      }

      onAccept() {
        this.acceptedAtInput.value = this.acceptInput.checked ? new Date().toISOString() : '';
        this.acceptedAtInput.disabled = !this.acceptInput.checked;
        this.acceptInput.removeAttribute('aria-invalid');
        this.hideMessage(this.acceptMessage);
        this.updateLock();
      }

      // Rewrites the single-item add (id, quantity, properties[...]) as items[0] = ring, items[1] = engraving service.
      onFormData(event) {
        if (!this.toggle.checked) return;

        const data = event.formData;
        const ringId = data.get('id');
        if (!ringId) return;

        const quantity = data.get('quantity') || '1';
        const ref = createRef();
        const ringEntries = Array.from(data.entries()).filter(
          ([key]) => key === 'id' || key === 'quantity' || key === 'selling_plan' || key.startsWith('properties[')
        );

        ringEntries.forEach(([key]) => data.delete(key));
        ringEntries.forEach(([key, value]) => {
          const name = key.startsWith('properties[')
            ? `items[0][properties]${key.slice('properties'.length)}`
            : `items[0][${key}]`;
          data.append(name, value);
        });
        if (!data.has('items[0][quantity]')) data.append('items[0][quantity]', quantity);
        data.append('items[0][properties][_grabado_ref]', ref);
        data.append('items[0][properties][_grabado_servicio]', this.dataset.serviceVariantId);

        data.append('items[1][id]', this.dataset.serviceVariantId);
        data.append('items[1][quantity]', quantity);
        data.append('items[1][properties][Para]', this.variantLabels[ringId] || '');
        data.append('items[1][properties][_grabado_ref]', ref);
      }

      updateCounter() {
        const length = Array.from(this.textInput.value).length;
        this.counter.textContent = length;
        this.counter.parentElement.classList.toggle('is-full', length >= this.textInput.maxLength);
      }

      isValid() {
        if (!this.toggle.checked) return true;
        return normalizeSpaces(this.textInput.value).length > 0 && this.acceptInput.checked;
      }

      // Runs on submit (capture phase). Cleans the text and, if something is missing, shows why and focuses it.
      validate() {
        if (!this.toggle.checked) return true;

        this.textInput.value = normalizeSpaces(sanitize(this.textInput.value));
        this.updateCounter();

        let firstInvalid = null;
        if (!this.textInput.value) {
          this.textInput.setAttribute('aria-invalid', 'true');
          this.showMessage(this.textMessage, this.dataset.messageEmpty, true);
          firstInvalid = this.textInput;
        }
        if (!this.acceptInput.checked) {
          this.acceptInput.setAttribute('aria-invalid', 'true');
          this.showMessage(this.acceptMessage, this.dataset.messageAccept, true);
          firstInvalid = firstInvalid || this.acceptInput;
        }

        this.updateLock();
        if (firstInvalid) firstInvalid.focus();
        return !firstInvalid;
      }

      // Buttons stay clickable (so a click explains what is missing) but look and announce as disabled.
      // The dynamic checkout button would skip the service line, so it is off while engraving is on.
      updateLock() {
        const locked = !this.isValid();
        this.form.classList.toggle('has-engraving', this.toggle.checked);

        this.submitButtons.forEach(button => {
          button.classList.toggle('is-engraving-locked', locked);
          if (locked) {
            button.setAttribute('aria-disabled', 'true');
            button.dataset.engravingLocked = '';
          } else if ('engravingLocked' in button.dataset) {
            button.removeAttribute('aria-disabled');
            delete button.dataset.engravingLocked;
          }
        });
      }

      get submitButtons() {
        const buttons = Array.from(this.form.querySelectorAll('button[type="submit"]'));
        if (this.form.id) {
          buttons.push(...document.querySelectorAll(`button[type="submit"][form="${CSS.escape(this.form.id)}"]`));
        }
        return buttons;
      }

      showMessage(element, text, isError = false) {
        element.textContent = text;
        element.classList.toggle('engraving-form__message--error', isError);
        element.hidden = false;
      }

      hideMessage(element) {
        element.textContent = '';
        element.hidden = true;
      }
    }

    customElements.define('engraving-form', EngravingForm);
  }

  // Capture phase on document: runs before the theme's own submit handlers (product-form, product-card).
  document.addEventListener(
    'submit',
    event => {
      const engravingForm = event.target.querySelector?.('engraving-form');
      if (!engravingForm?.initialized || engravingForm.validate()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  /* ---------- Carrito: editar y quitar el grabado (snippets/line-item.liquid) ---------- */

  const fetchCart = () =>
    fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } }).then(response =>
      response.json()
    );

  async function postCart(path, body) {
    const response = await fetch(`${window.Shopify.routes.root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const state = await response.json();
    if (!response.ok || state.status) throw new Error(state.description || `${path} failed`);
    return state;
  }

  // Same re-render as CartItems.updateQuantity in cart.js. Without a cart element to render into, reload.
  function renderCartSections(cartItems, state) {
    if (!cartItems || !state?.sections) {
      window.location.reload();
      return;
    }
    cartItems.getSectionsToRender().forEach(section => {
      const element = document.querySelector(section.selector) || document.querySelector(section.id);
      const html = state.sections[section.section];
      if (element && html) element.innerHTML = cartItems.getSectionInnerHTML(html, section.selector);
    });
    if (typeof updateCartCounters === 'function') updateCartCounters();
    if (typeof updateFreeShipping === 'function') updateFreeShipping();
  }

  // cart/change.js does not clear a line's properties (an empty properties object is ignored), so the ring is
  // removed and added again without the engraving properties. If the add fails, the original line is restored.
  async function stripEngraving(item, extraParams = {}) {
    const properties = { ...item.properties };
    ENGRAVING_PROPERTIES.forEach(key => delete properties[key]);

    const line = { id: item.variant_id, quantity: item.quantity, properties };
    if (item.selling_plan_allocation) line.selling_plan = item.selling_plan_allocation.selling_plan.id;

    await postCart('cart/change.js', { id: item.key, quantity: 0 });
    try {
      return await postCart('cart/add.js', { items: [line], ...extraParams });
    } catch (error) {
      await postCart('cart/add.js', { items: [{ ...line, properties: item.properties }] });
      throw error;
    }
  }

  const sectionsParams = cartItems => ({
    sections: cartItems?.getSectionsToRender?.().map(section => section.section) || [],
    sections_url: window.location.pathname
  });

  // The edited line is re-rendered with a new key, so screen reader feedback goes through a page-level live region.
  function announce(text) {
    let region = document.getElementById('EngravingLiveRegion');
    if (!region) {
      region = document.createElement('p');
      region.id = 'EngravingLiveRegion';
      region.className = 'visually-hidden';
      region.setAttribute('aria-live', 'polite');
      document.body.append(region);
    }
    region.textContent = '';
    setTimeout(() => {
      region.textContent = text;
    }, 100);
  }

  if (!customElements.get('engraving-cart-edit')) {
    class EngravingCartEdit extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.openButton = this.querySelector('[data-engraving-edit-open]');
        this.panel = this.querySelector('[data-engraving-edit-panel]');
        this.textInput = this.querySelector('[data-engraving-edit-text]');
        this.counter = this.querySelector('[data-engraving-counter]');
        this.message = this.querySelector('[data-engraving-edit-message]');
        this.status = this.querySelector('[data-engraving-edit-status]');
        this.saveButton = this.querySelector('[data-engraving-edit-save]');
        this.originalText = this.textInput.value;

        this.openButton.addEventListener('click', () => this.setOpen(true));
        this.querySelector('[data-engraving-edit-cancel]').addEventListener('click', () => this.cancel());
        this.saveButton.addEventListener('click', () => this.save());
        this.textInput.addEventListener('input', this.onTextInput.bind(this));
        this.textInput.addEventListener('keydown', event => {
          // Enter would submit the cart form (checkout on the cart page).
          if (event.key === 'Enter') {
            event.preventDefault();
            this.save();
          } else if (event.key === 'Escape') {
            this.cancel();
          }
        });
        // The cart drawer closes on Escape keyup: keep it open while editing.
        this.textInput.addEventListener('keyup', event => {
          if (event.key === 'Escape') event.stopPropagation();
        });

        this.updateCounter();
      }

      setOpen(open) {
        this.panel.hidden = !open;
        this.openButton.hidden = open;
        this.openButton.setAttribute('aria-expanded', open);
        this.hide(this.status);
        if (open) {
          this.textInput.focus();
          this.textInput.select();
        }
      }

      cancel() {
        this.textInput.value = this.originalText;
        this.textInput.removeAttribute('aria-invalid');
        this.hide(this.message);
        this.updateCounter();
        this.setOpen(false);
        this.openButton.focus();
      }

      onTextInput() {
        const { value } = this.textInput;
        const sanitized = sanitize(value);

        if (sanitized !== value) {
          const caret = sanitize(value.slice(0, this.textInput.selectionStart ?? value.length)).length;
          this.textInput.value = sanitized;
          this.textInput.setSelectionRange(caret, caret);
        }
        if (removedChars(value, sanitized)) {
          this.show(this.message, this.dataset.messageSanitized);
        } else {
          this.hide(this.message);
        }
        this.textInput.removeAttribute('aria-invalid');
        this.updateCounter();
      }

      updateCounter() {
        const length = Array.from(this.textInput.value).length;
        this.counter.textContent = length;
        this.counter.parentElement.classList.toggle('is-full', length >= this.textInput.maxLength);
      }

      async save() {
        if (this.saving) return;

        const text = normalizeSpaces(sanitize(this.textInput.value));
        this.textInput.value = text;
        this.updateCounter();

        if (!text) {
          this.textInput.setAttribute('aria-invalid', 'true');
          this.show(this.message, this.dataset.messageEmpty, true);
          this.textInput.focus();
          return;
        }
        if (text === this.originalText) {
          this.cancel();
          return;
        }

        const cartItems = this.closest('cart-drawer-items') || this.closest('cart-items');
        this.saving = true;
        this.saveButton.setAttribute('aria-disabled', 'true');
        cartItems?.classList.add('is-loading');

        try {
          const cart = await fetchCart();
          const item = cart.items.find(cartItem => cartItem.key === this.dataset.lineKey);
          if (!item) throw new Error('line not found');

          // cart/change replaces every property, so the internal ones (_grabado_ref...) are sent again untouched.
          const state = await postCart('cart/change.js', {
            id: item.key,
            quantity: item.quantity,
            properties: { ...item.properties, Grabado: text },
            ...sectionsParams(cartItems)
          });
          renderCartSections(cartItems, state);

          // The line was re-rendered with a new key: find it by its ref to confirm and keep the focus there.
          const updated = document.querySelector(
            `${cartItems?.localName} engraving-cart-edit[data-ref="${CSS.escape(this.dataset.ref)}"]`
          );
          if (updated) {
            updated.show(updated.status, this.dataset.messageSaved);
            updated.openButton.focus();
          }
        } catch (error) {
          console.error('[engraving] edit failed:', error);
          this.show(this.message, this.dataset.messageError, true);
        } finally {
          this.saving = false;
          this.saveButton.removeAttribute('aria-disabled');
          cartItems?.classList.remove('is-loading');
        }
      }

      show(element, text, isError = false) {
        element.textContent = text;
        element.classList.toggle('engraving-form__message--error', isError);
        element.hidden = false;
      }

      hide(element) {
        element.textContent = '';
        element.hidden = true;
      }
    }

    customElements.define('engraving-cart-edit', EngravingCartEdit);
  }

  if (!customElements.get('engraving-cart-remove')) {
    class EngravingCartRemove extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.openButton = this.querySelector('[data-engraving-remove-open]');
        this.confirm = this.querySelector('[data-engraving-remove-confirm]');
        this.yesButton = this.querySelector('[data-engraving-remove-yes]');
        this.message = this.querySelector('[data-engraving-remove-message]');

        this.openButton.addEventListener('click', () => this.setOpen(true));
        this.querySelector('[data-engraving-remove-no]').addEventListener('click', () => this.close());
        this.yesButton.addEventListener('click', () => this.remove());
        // Escape closes the confirmation; the cart drawer (which closes on Escape keyup) stays open.
        this.confirm.addEventListener('keyup', event => {
          if (event.key !== 'Escape') return;
          event.stopPropagation();
          this.close();
        });
      }

      setOpen(open) {
        this.confirm.hidden = !open;
        this.openButton.hidden = open;
        this.openButton.setAttribute('aria-expanded', open);
        this.message.hidden = true;
        if (open) this.yesButton.focus();
      }

      close() {
        this.setOpen(false);
        this.openButton.focus();
      }

      // Removes the service line(s) and leaves the ring in the cart without engraving.
      async remove() {
        if (this.removing) return;

        const cartItems = this.closest('cart-drawer-items') || this.closest('cart-items');
        this.removing = true;
        this.yesButton.setAttribute('aria-disabled', 'true');
        cartItems?.classList.add('is-loading');

        try {
          const { ref } = this.dataset;
          const cart = await fetchCart();
          const ring = cart.items.find(item => !isService(item) && item.properties?._grabado_ref === ref);
          const services = cart.items.filter(item => isService(item) && item.properties?._grabado_ref === ref);

          if (!ring && !services.length) throw new Error('engraving lines not found');

          // Services first. If stripping the ring then fails, the ring is restored and the sync in the catch below
          // adds its service back, so an engraving never stays in the cart unpaid.
          let state;
          for (const [index, service] of services.entries()) {
            const isLast = !ring && index === services.length - 1;
            const step = { id: service.key, quantity: 0 };
            state = await postCart('cart/change.js', isLast ? { ...step, ...sectionsParams(cartItems) } : step);
          }
          if (ring) state = await stripEngraving(ring, sectionsParams(cartItems));

          renderCartSections(cartItems, state);
          announce(this.dataset.messageRemoved);
        } catch (error) {
          console.error('[engraving] remove failed:', error);
          this.message.textContent = this.dataset.messageError;
          this.message.hidden = false;
          syncEngravingLines();
        } finally {
          this.removing = false;
          this.yesButton.removeAttribute('aria-disabled');
          cartItems?.classList.remove('is-loading');
        }
      }
    }

    customElements.define('engraving-cart-remove', EngravingCartRemove);
  }

  /* ---------- Carrito: sincronización anillo <-> servicio de grabado ---------- */

  let syncing = false;
  let lastFix = null;

  const isService = item => item.handle === SERVICE_HANDLE;

  const ringLabel = item =>
    item.product_has_only_default_variant
      ? item.product_title
      : [
          item.product_title,
          ...item.options_with_values.map(
            option => `${option.name.charAt(0)}${option.name.slice(1).toLowerCase()}: ${option.value}`
          )
        ].join(' · ');

  // Returns the first fix the cart needs, or null. One fix at a time: each one re-renders the cart and syncs again.
  function nextFix(cart) {
    const lines = cart.items.map((item, index) => ({ item, line: index + 1, props: item.properties || {} }));
    const rings = lines.filter(({ item, props }) => !isService(item) && ENGRAVING_PROPERTIES.some(key => props[key]));
    const services = lines.filter(({ item }) => isService(item));

    const invalidRing = rings.find(({ props }) => !(props.Grabado && props._grabado_aceptado && props._grabado_ref));
    if (invalidRing) return { type: 'strip', item: invalidRing.item };

    const linkedRefs = new Set();
    for (const { item, line, props } of services) {
      const ring = rings.find(r => r.props._grabado_ref === props._grabado_ref);
      if (!ring || linkedRefs.has(props._grabado_ref)) return { type: 'quantity', line, quantity: 0 };
      linkedRefs.add(props._grabado_ref);
      if (item.quantity !== ring.item.quantity) return { type: 'quantity', line, quantity: ring.item.quantity };
    }

    const unpaidRing = rings.find(({ props }) => !linkedRefs.has(props._grabado_ref));
    if (unpaidRing) {
      if (!unpaidRing.props._grabado_servicio) {
        return { type: 'strip', item: unpaidRing.item };
      }
      return {
        type: 'add',
        id: Number(unpaidRing.props._grabado_servicio),
        quantity: unpaidRing.item.quantity,
        properties: { Para: ringLabel(unpaidRing.item), _grabado_ref: unpaidRing.props._grabado_ref }
      };
    }

    return null;
  }

  async function syncEngravingLines() {
    if (syncing) return;
    syncing = true;
    let resync = false;

    try {
      const cart = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' }
      }).then(response => response.json());

      const fix = nextFix(cart);
      const signature = JSON.stringify(fix);
      if (!fix || signature === lastFix) {
        // Same fix twice in a row means it did not stick: stop instead of looping.
        lastFix = null;
        return;
      }
      lastFix = signature;
      resync = await applyFix(fix);
    } catch (error) {
      console.error('[engraving] cart sync failed:', error);
    } finally {
      syncing = false;
    }

    if (resync) syncEngravingLines();
  }

  // Returns true when the caller has to sync again (the theme's cart update already publishes cartUpdate by itself).
  async function applyFix(fix) {
    const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');

    // Quantity fixes reuse the theme's own cart update, which re-renders the cart and publishes cartUpdate again.
    if (fix.type === 'quantity' && cartItems) {
      cartItems.updateQuantity(fix.line, fix.quantity);
      return false;
    }

    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer?.getSectionsToRender?.().map(section => section.section) || [];
    const params = sections.length ? { sections, sections_url: window.location.pathname } : {};

    let state;
    if (fix.type === 'quantity') {
      state = await postCart('cart/change.js', { line: fix.line, quantity: fix.quantity, ...params });
    } else if (fix.type === 'strip') {
      state = await stripEngraving(fix.item, params);
    } else {
      state = await postCart('cart/add.js', {
        items: [{ id: fix.id, quantity: fix.quantity, properties: fix.properties }],
        ...params
      });
    }

    if (cartDrawer && state.sections) {
      cartDrawer.renderContents(state, false);
      return true;
    }
    if (cartItems) window.location.reload();
    return false;
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      if (event?.source === 'cart-items') syncEngravingLines();
    });
  }

  // theme.liquid only flags the script when the cart has engraving lines, so normal carts cost no extra request.
  if (engravingScript?.hasAttribute('data-engraving-sync')) syncEngravingLines();
})();
